import { HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { generateRandomVeryLargePositiveInteger } from './utils';

/** Serialized request stored in persistent queue (Ionic Storage). */
export interface StoredQueuedRequest {
  id: string;
  method: string;
  url: string;
  body: unknown;
  createdAt: number;
}

export interface CachePatchStrategy {
    /** Which cached URL to patch */
    cacheUrlMatcher: (reqUrl: string) => string | null;
    /** How to apply the queued request's body onto the cached response body */
    applyPatch: (cachedBody: any, queuedBody: any) => any;
}
  
export const CACHE_PATCH_STRATEGIES: Record<string, CachePatchStrategy> = {
    '/api/rpc/r_set_work_order_status': {
        cacheUrlMatcher: (_reqUrl) => '/api/rpc/r_get_work_orders', // or derive dynamically
        applyPatch: (cachedBody: any, queuedBody: any) => {
            return {
                ...cachedBody,
                zadaci: cachedBody.zadaci.map((zadatak) =>
                zadatak.pk === queuedBody.p_zad_pk
                    ? { ...zadatak, status: queuedBody.p_status_naloga, ciklus: [
                         { pk: generateRandomVeryLargePositiveInteger(), status: queuedBody.p_status_naloga, datum_statusa: (new Date()).toISOString(), napomena: queuedBody.p_napomena },
                         ...zadatak.ciklus] }
                    : zadatak
                ),
            };
        },
    },
    '/api/rpc/r_set_notification_status': {
        cacheUrlMatcher: (_reqUrl) => '/api/rpc/r_get_notifications', // or derive dynamically
        applyPatch: (cachedBody: any, queuedBody: any) => {
            return cachedBody.map((notification: any) =>
                    notification.pk === queuedBody.p_pk ? { ...notification, status: queuedBody.p_status } : notification);
        },
    },
};

const REQUEST_QUEUE_KEY = 'offline_request_queue';

@Injectable({
  providedIn: 'root',
})
export class OfflineService {
  storage = inject(Storage);

  constructor() {}

  async setCachedResponse(url: string, response: unknown): Promise<void> {
    await this.storage.set(`cached_response_${url}`, { response, timestamp: Date.now() });
  }

  async getCachedResponse(url: string): Promise<{ response: unknown; timestamp: number } | null> {
    return this.storage.get(`cached_response_${url}`);
  }

  async removeCachedResponse(url: string): Promise<void> {
    await this.storage.remove(`cached_response_${url}`);
  }

  /** Get all queued requests from storage. */
  async getQueuedRequests(): Promise<StoredQueuedRequest[]> {
    const queue = await this.storage.get(REQUEST_QUEUE_KEY);
    return Array.isArray(queue) ? queue : [];
  }

  /** Append a request to the persistent queue. */
  async addQueuedRequest(item: StoredQueuedRequest): Promise<void> {
    const queue = await this.getQueuedRequests();
    queue.push(item);
    await this.storage.set(REQUEST_QUEUE_KEY, queue);
  }

  /** Remove and return the first queued request. */
  async removeFirstQueuedRequest(): Promise<StoredQueuedRequest | null> {
    const queue = await this.getQueuedRequests();
    if (queue.length === 0) return null;
    const removed = queue.shift()!;
    await this.storage.set(REQUEST_QUEUE_KEY, queue);
    return removed;
  }

  /** Re-add a request at the front of the queue (e.g. after retry failed). */
  async addQueuedRequestAtFront(item: StoredQueuedRequest): Promise<void> {
    const queue = await this.getQueuedRequests();
    queue.unshift(item);
    await this.storage.set(REQUEST_QUEUE_KEY, queue);
  }

  /** Remove a queued request by id. */
  async removeQueuedRequestById(id: string): Promise<void> {
    const queue = await this.getQueuedRequests();
    const filtered = queue.filter((r) => r.id !== id);
    if (filtered.length !== queue.length) {
      await this.storage.set(REQUEST_QUEUE_KEY, filtered);
    }
  }

  async patchCache(request: HttpRequest<unknown>): Promise<void> {
    const matchedKey = Object.keys(CACHE_PATCH_STRATEGIES).find((pattern) =>
      request.url.includes(pattern)
    );
    if (!matchedKey) return;
  
    const strategy = CACHE_PATCH_STRATEGIES[matchedKey];
    const cacheUrl = strategy.cacheUrlMatcher(request.url);
    if (!cacheUrl) return;
  
    const cached = await this.getCachedResponse(cacheUrl);
    if (!cached) return;
  
    const patched = strategy.applyPatch(cached.response, request.body);
    await this.setCachedResponse(cacheUrl, patched);
  }
}